import { FC, useCallback } from 'react';
import {
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Checkbox,
  FormGroup,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import type { QuestionChoice } from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';
import { isExclusiveChoice } from 'src/utils/exclusiveChoice';

interface AnswerInputProps {
  questionType: string;
  questionLabel: string;
  value: string;
  choices: QuestionChoice[];
  selectedChoiceIds: number[];
  onChange: (value: string, choiceIds?: number[]) => void;
  onBlur: () => void;
  disabled?: boolean;
  error?: string | null;
  helpText?: string;
}

const TextInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    value={value}
    onChange={(e) => {
      onChange(e.target.value);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const TextAreaInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    multiline
    minRows={3}
    value={value}
    onChange={(e) => {
      onChange(e.target.value);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const CurrencyInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    value={value}
    onChange={(e) => {
      const raw = e.target.value.replace(/[^0-9.\-()]/g, '');
      onChange(raw);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    slotProps={{
      input: {
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      },
      htmlInput: { 'aria-label': questionLabel },
    }}
  />
);

const NumberInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    type="text"
    inputMode="numeric"
    value={value}
    onChange={(e) => {
      const raw = e.target.value.replace(/[^0-9.\-]/g, '');
      onChange(raw);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const PercentageInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    type="text"
    inputMode="decimal"
    value={value}
    onChange={(e) => {
      const raw = e.target.value.replace(/[^0-9.]/g, '');
      onChange(raw);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    slotProps={{
      input: { endAdornment: '%' },
      htmlInput: { 'aria-label': questionLabel },
    }}
  />
);

const BoolInput: FC<AnswerInputProps> = ({
  questionLabel,
  choices,
  selectedChoiceIds,
  onChange,
  onBlur,
  disabled,
}) => {
  const handleChange = (choiceId: number): void => {
    onChange('', [choiceId]);
  };

  return (
    <RadioGroup
      aria-label={questionLabel}
      value={selectedChoiceIds[0] ?? ''}
      onChange={(_, val) => {
        handleChange(Number(val));
      }}
      onBlur={onBlur}
      row
    >
      {choices.map((choice) => (
        <FormControlLabel
          key={choice.id}
          value={choice.id}
          control={<Radio size="small" />}
          label={choice.name}
          disabled={disabled}
        />
      ))}
    </RadioGroup>
  );
};

const ChoicesInput: FC<AnswerInputProps> = ({
  questionLabel,
  choices,
  selectedChoiceIds,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => {
  const selected =
    choices.find((c) => selectedChoiceIds.includes(c.id)) ?? null;

  return (
    <Autocomplete
      size="small"
      options={choices}
      getOptionLabel={(opt) => opt.name}
      value={selected}
      onChange={(_, val) => {
        onChange('', val ? [val.id] : []);
      }}
      onBlur={onBlur}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error || helpText}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              'aria-label': questionLabel,
            },
          }}
        />
      )}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
    />
  );
};

const COLUMN_SIZE = 10;

const MultiselectInput: FC<AnswerInputProps> = ({
  questionLabel,
  choices,
  selectedChoiceIds,
  onChange,
  onBlur,
  disabled,
}) => {
  const handleToggle = (choiceId: number): void => {
    const isSelected = selectedChoiceIds.includes(choiceId);

    if (isSelected) {
      onChange('', selectedChoiceIds.filter((id) => id !== choiceId));
      return;
    }

    const choice = choices.find((c) => c.id === choiceId);

    if (isExclusiveChoice(choice)) {
      // Selecting an exclusive choice clears every other selection.
      onChange('', [choiceId]);
      return;
    }

    // Selecting a normal choice clears any currently-selected exclusive
    // choice ("I do not have any X") before adding the new one.
    const filtered = selectedChoiceIds.filter((id) => {
      const c = choices.find((ch) => ch.id === id);
      return !isExclusiveChoice(c);
    });
    onChange('', [...filtered, choiceId]);
  };

  return (
    <FormGroup
      aria-label={questionLabel}
      onBlur={onBlur}
      sx={{
        display: 'grid',
        gridTemplateRows: {
          xs: 'none',
          md: `repeat(${COLUMN_SIZE}, auto)`,
        },
        gridAutoFlow: { xs: 'row', md: 'column' },
        gridAutoColumns: { md: '1fr' },
      }}
    >
      {choices.map((choice) => (
        <FormControlLabel
          key={choice.id}
          control={
            <Checkbox
              size="small"
              checked={selectedChoiceIds.includes(choice.id)}
              onChange={() => {
                handleToggle(choice.id);
              }}
              disabled={disabled}
            />
          }
          label={choice.name}
        />
      ))}
    </FormGroup>
  );
};

const DateInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => {
  const dateValue = value ? dayjs(value) : null;

  const handleDateChange = useCallback(
    (newValue: Dayjs | null): void => {
      onChange(newValue ? newValue.format('YYYY-MM-DD') : '');
    },
    [onChange]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={dateValue}
        onChange={handleDateChange}
        disabled={disabled}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            error: !!error,
            helperText: error || helpText,
            onBlur,
            slotProps: { htmlInput: { 'aria-label': questionLabel } },
          },
        }}
      />
    </LocalizationProvider>
  );
};

const SSNInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    value={value}
    onChange={(e) => {
      const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
      onChange(raw);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    placeholder="XXX-XX-XXXX"
    autoComplete="one-time-code"
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const LastSSNInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    value={value}
    onChange={(e) => {
      const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
      onChange(raw);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    placeholder="Last 4 digits"
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const PhoneInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    type="tel"
    value={value}
    onChange={(e) => {
      onChange(e.target.value);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    placeholder="(XXX) XXX-XXXX"
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const EmailInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    type="email"
    value={value}
    onChange={(e) => {
      onChange(e.target.value);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const ZipCodeInput: FC<AnswerInputProps> = ({
  value,
  questionLabel,
  onChange,
  onBlur,
  disabled,
  error,
  helpText,
}) => (
  <TextField
    fullWidth
    size="small"
    value={value}
    onChange={(e) => {
      const raw = e.target.value.replace(/[^0-9\-]/g, '').slice(0, 10);
      onChange(raw);
    }}
    onBlur={onBlur}
    disabled={disabled}
    error={!!error}
    helperText={error || helpText}
    placeholder="XXXXX"
    slotProps={{ htmlInput: { 'aria-label': questionLabel } }}
  />
);

const ReadOnlyDisplay: FC<{ value: string; label?: string }> = ({
  value,
  label,
}) => (
  <TextField
    fullWidth
    size="small"
    value={value || '—'}
    disabled
    label={label}
  />
);

const INPUT_MAP: Record<string, FC<AnswerInputProps>> = {
  text: TextInput,
  log_number: TextInput,
  regex: TextInput,
  name: TextInput,
  account: TextInput,
  text_area: TextAreaInput,
  currency: CurrencyInput,
  number: NumberInput,
  percentage: PercentageInput,
  bool: BoolInput,
  choices: ChoicesInput,
  multiselect: MultiselectInput,
  frequency: ChoicesInput,
  date: DateInput,
  ssn: SSNInput,
  last_SSN: LastSSNInput,
  phone: PhoneInput,
  email: EmailInput,
  zip_code: ZipCodeInput,
};

interface QuestionnaireAnswerInputProps {
  questionType: string;
  questionLabel: string;
  value: string;
  choices: QuestionChoice[];
  selectedChoiceIds: number[];
  onChange: (value: string, choiceIds?: number[]) => void;
  onBlur: () => void;
  disabled?: boolean;
  error?: string | null;
  helpText?: string;
}

const QuestionnaireAnswerInput: FC<QuestionnaireAnswerInputProps> = (props) => {
  const { questionType } = props;

  if (questionType === 'calculation') {
    return <ReadOnlyDisplay value={props.value} />;
  }

  const InputComponent = INPUT_MAP[questionType] ?? TextInput;
  return <InputComponent {...props} />;
};

export default QuestionnaireAnswerInput;
